import { Location } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { ApplicationContext, UserType } from 'src/app/models';
import { AuthData } from 'src/app/models/auth/auth-data.model';
import { AuthenticationUserCompany } from 'src/app/models/auth/authentication-response.model';
import { ApplicationContextService } from 'src/app/services/application-context.service';
import { AuthService } from 'src/app/services/auth.service';
import { CacheService } from 'src/app/services/cache.service';
import { Constants } from 'src/app/services/constants';
import { NavigationService } from 'src/app/services/navigation.service';
import { NotificationService } from 'src/app/services/notification.service';
import { hasUsableAuthTokens } from 'src/app/shared/utils/auth-token-utils';

@Component({
    selector: 'company-switch',
    templateUrl: 'company-switch.component.html',
    styleUrls: ['./company-switch.component.scss'],
    styles: [`
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      :host ::ng-deep .tpo-loader-spin svg {
        animation: spin 1.2s linear infinite;
      }
    `],
    standalone: false
})
export class CompanySwitchComponent implements OnDestroy {

  company: AuthenticationUserCompany;
  currentYear: number = new Date().getFullYear();

  private readonly _activatedRouteSubscription: Subscription;
  private _applicationContextSubscription: Subscription;
  private _applicationContextSubscription2: Subscription;

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _navigationService: NavigationService,
    private readonly _cacheService: CacheService,
    private readonly _authService: AuthService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _notifsService: NotificationService,
    private readonly _location: Location
  ) {
    this._activatedRouteSubscription = this._activatedRoute.queryParams.subscribe((params) => {
      const companyGuid = params[Constants.authorization.companyGuid];
      const userType = params[Constants.authorization.userType];
      const userCompanyGuid = params[Constants.authorization.userCompanyGuid];

      this._applicationContextSubscription2 = this._applicationContextService.context.subscribe(context => {
        if (context.userPermissions.superAdmin) {
          const companyForAdmin = context.currentlyLoggedInUser.allUserCompanyData.find(c => c.companyGuid === companyGuid);
          if (!companyForAdmin) {
            throw new Error("Company not found");
          }

          this.company = {
            companyGuid: companyForAdmin.companyGuid,
            companyId: companyForAdmin.companyId,
            companyName: companyForAdmin.companyName,
            companyUrl: companyForAdmin.companyUrl,
            roleId: null,
            roleName: null,
            userType: UserType.Admin
          };
        } else {
          this.company = context.currentlyLoggedInUser.allUserCompanyData.find(c => c.companyGuid === companyGuid && c.userType === userType);
        }
        this.switchToCompany(this.company.companyGuid, this.company.userType, userCompanyGuid || '');
      });
    });
  }

  ngOnDestroy(): void {
    this._activatedRouteSubscription?.unsubscribe();
    this._applicationContextSubscription?.unsubscribe();
    this._applicationContextSubscription2?.unsubscribe();
  }

  private switchToCompany = (companyGuid: string, userType: UserType, userCompanyGuid: string) => {
    const authData: AuthData = this._localStorageService.authorizationData;
    this._authService.exchangeToken(companyGuid, userType, userCompanyGuid).subscribe(response => {
      if (!hasUsableAuthTokens(response)) {
        // Writing a response that is missing either token would replace the credentials still in storage
        // with undefined, logging the user out on their next token refresh.
        this._notifsService.showError('Error switching companies. Please try again.', 'Error!');
        this._location.back();
        return;
      }

      authData.token = response.jwtToken;
      authData.refreshToken = response.refreshToken;
      authData.companyGuid = companyGuid;
      // The identity the exchanged token carries, kept in storage the way every other token-exchange flow
      // (impersonation, TPO) does - anything keyed off the stored identity would otherwise still read the
      // company this user was in before the switch.
      authData.userCompanyGuid = response.userCompanyGuid;
      this._cacheService.setTenantPrefix(response.userCompanyGuid);
      // Company switching is a new login locally, so it must not inherit the previous session's marker.
      authData.clientSessionId = undefined;
      this._localStorageService.authorizationData = authData;
      this._applicationContextService.reset();
      this._applicationContextSubscription = this._applicationContextService.context.subscribe(context => {
        this.onAfterLogin(context);
      });
    });
  }

  private onAfterLogin = (context: ApplicationContext) => {
    if (context.currentlyLoggedInUser.userType === UserType.Tpo) {
      this._navigationService.navigateToPath('tpo');
    } else if (context.userPermissions.companyId === 1) {
      this._navigationService.navigateToPath('global-admin/companies');
    } else {
      this._navigationService.navigateToPath('admin');
    }
  };
}
